/**
 * Palette Thumbnail Web Component
 * 
 * A web component that displays a palette preview using the compact SVG
 * from design_pattern.palette_thumbnail with colors filled in.
 * Uses Shadow DOM to isolate styles and prevent conflicts with configurator-preview.
 * 
 * Usage:
 * <palette-thumbnail 
 *   svg-url="https://..."
 *   colors='["#ff0000", "#00ff00", "#0000ff"]'
 *   color-slots='["color_1", "color_2", "color_3"]'>
 * </palette-thumbnail>
 */

// Global SVG cache shared across all instances
const svgCache = new Map();
// Track ongoing fetch requests to prevent duplicate fetches
const pendingFetches = new Map();

class PaletteThumbnail extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() {
    return ['svg-url', 'colors', 'color-slots'];
  }

  connectedCallback() {
    this.scheduleRender();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.scheduleRender();
    }
  }

  // The three attributes are set one after the other, before the element is connected: each change
  // used to trigger a render, so every thumbnail was built twice. Render once, when connected.
  scheduleRender() {
    if (this._renderQueued) return;
    this._renderQueued = true;
    queueMicrotask(() => {
      this._renderQueued = false;
      if (this.isConnected) this.render();
    });
  }

  async render() {
    const svgUrl = this.getAttribute('svg-url');
    const colorsAttr = this.getAttribute('colors');
    const colorSlotsAttr = this.getAttribute('color-slots');

    if (!svgUrl || !colorsAttr || !colorSlotsAttr) {
      this.shadowRoot.innerHTML = this._getPlaceholder();
      return;
    }

    try {
      const colors = JSON.parse(colorsAttr);
      const colorSlots = JSON.parse(colorSlotsAttr);

      // Load SVG using global cache
      const svgContent = await this._loadSVG(svgUrl);

      // Create the rendered content
      this.shadowRoot.innerHTML = this._createContent(svgContent, colors, colorSlots);
    } catch (error) {
      console.error('Error rendering palette thumbnail:', error);
      this.shadowRoot.innerHTML = this._getErrorState();
    }
  }

  async _loadSVG(url) {
    // Check if already cached
    if (svgCache.has(url)) {
      return svgCache.get(url);
    }

    // Check if there's already a pending fetch for this URL
    if (pendingFetches.has(url)) {
      return pendingFetches.get(url);
    }

    // Create new fetch promise
    const fetchPromise = (async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const svgContent = await response.text();
        
        // Cache the result
        svgCache.set(url, svgContent);
        
        return svgContent;
      } catch (error) {
        console.error('Error loading SVG:', error);
        throw error;
      } finally {
        // Clean up pending fetch
        pendingFetches.delete(url);
      }
    })();

    // Store the pending fetch
    pendingFetches.set(url, fetchPromise);

    return fetchPromise;
  }

  _createContent(svgContent, colors, colorSlots) {
    // Build CSS for color mapping
    let cssRules = '';
    colorSlots.forEach((slot, index) => {
      const cleanSlot = slot.trim();
      const color = colors[index] ? colors[index].trim() : '#cccccc';
      cssRules += `.${cleanSlot} { fill: ${color}; }\n`;
    });

    return `
      <style>
        :host {
          display: block;
          width: 100%;
          height: 100%;
        }
        
        .palette-thumbnail-container {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        
        svg {
          width: 100%;
          height: 100%;
          display: block;
        }
        
        /* Color slot styles */
        ${cssRules}
      </style>
      <div class="palette-thumbnail-container">
        ${svgContent}
      </div>
    `;
  }

  _getPlaceholder() {
    return `
      <style>
        :host {
          display: block;
          width: 100%;
          height: 100%;
        }
        .placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f5f5f5;
          color: #999;
          font-size: 12px;
        }
      </style>
      <div class="placeholder">Loading...</div>
    `;
  }

  _getErrorState() {
    return `
      <style>
        :host {
          display: block;
          width: 100%;
          height: 100%;
        }
        .error {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fee;
          color: #c33;
          font-size: 12px;
        }
      </style>
      <div class="error">Failed to load</div>
    `;
  }
}

// Register the custom element
if (!customElements.get('palette-thumbnail')) {
  customElements.define('palette-thumbnail', PaletteThumbnail);
}

export { PaletteThumbnail };
